import {
  Box,
  Button,
  Card,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { useMention } from "../context/MentionContext";
import { useMentionInput } from "../Hooks/useMentionInput";
import apiService from "../Services/api.service";
import CustomButton from "./Button";
import MentionDropdown from "./MentionDropdown";
import { useToast } from "./Toast"; 
const AddStage = ({
  setIsLoader,
  doctorName,
  doctorId,
  stages,
  fetchEngagement,
  setAvailableStages,
}) => {
  const token = localStorage.getItem("token");
  const [clientSatisfaction, setClientSatisfaction] = useState("");
  const [feedback, setFeedback] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [newCustomStage, setNewCustomStage] = useState("");
  const [newStage, setNewStage] = useState("");
  const [description, setDescription] = useState("");
  const [authEntityType, setAuthEntityType] = useState("ops"); 
  const { mentions, resetMentions } = useMention();
  const { getMentionChangeHandler } = useMentionInput();

  const descriptionRef = React.useRef(null);
  const feedbackRef = React.useRef(null);

  const Toast = useToast();
  const handleAddStage = async () => {
    if (!newStage) {
      Toast.error("Please select a Title.");
      return;
    }
 

    if (
      clientSatisfaction &&
      (clientSatisfaction > 5 || clientSatisfaction < 1)
    ) {
      Toast.error("Client Satisfaction should be a number between 1 and 5.");
      return;
    }

    const payload = {
      doctorId,
      stage: newStage,
      description, 
      authEntityType,
      clientSatisfaction,
      feedback,
    };

    try {
      setIsLoader(true);
      // 1️⃣ Save stage
      await apiService.addEngagementStage(payload, token);

      // 2️⃣ Extract mentions from description + feedback
      const mentionedUsernames = [
        ...extractMentionedUsernames(description),
        ...extractMentionedUsernames(feedback),
      ];

      // 3️⃣ Match with mention context
      const taggedUsers = mentions.filter((u) =>
        mentionedUsernames.includes(u.username)
      );

      // 4️⃣ Fire tag notifications
      if (taggedUsers.length > 0) {
        await Promise.all(
          taggedUsers.map((user) =>
            apiService.addTag({
              receiverId: user._id,
              module: "Engagement",
              redirectUrl: `/dashboard/engagementTracker/${doctorId}`,
              message: `You were tagged in an engagement Timeline of ${doctorName}`,
              token,
            })
          )
        );
      }

      // 5️⃣ Reset form + mentions
      setNewStage("");
      setDescription("");
      setFeedback(""); 
      setClientSatisfaction("");
      resetMentions();

      // 6️⃣ Refresh timeline
      await fetchEngagement();
    } catch (err) {
      console.error("Error adding stage:", err);
      Toast.error("Failed to add stage");
    } finally {
      setIsLoader(false);
    }
  };

  const extractMentionedUsernames = (text = "") => {
    const matches = text.match(/@([\w.-]+)/g) || [];
    return matches.map((m) => m.substring(1));
  };

  const handleAddCustomStage = async () => {
    const stageName = newCustomStage.trim();
    if (!stageName) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/stage/add/${doctorId}`,
        {
          stage: stageName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Add to local state
      const newStageObj = {
        key: stageName.toUpperCase().replace(/\s/g, "_"),
        value: stageName,
        type: "custom",
      };
      setAvailableStages((prev) => [...prev, newStageObj]);
      setNewCustomStage("");
      setShowInput(false);
      setNewStage(stageName); // optionally select it
    } catch (err) {
      console.error("Failed to add custom stage", err);
    }
  };

  return (
    <Card sx={{ p: 4, borderRadius: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Add New
      </Typography>

      <Stack spacing={3} maxWidth={600} mt={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Select
            value={newStage}
            onChange={(e) => setNewStage(e.target.value)}
            displayEmpty
            fullWidth
            variant="outlined"
          >
            <MenuItem value="" disabled>
              Select Title
            </MenuItem>
            {[
              ...stages.filter((s) => s.type === "default"),
              ...stages.filter((s) => s.type === "custom"),
            ].map((stage) => (
              <MenuItem key={stage.key} value={stage.value}>
                {stage.value}
              </MenuItem>
            ))}
          </Select>

          {!showInput ? (
            <Button
              onClick={() => setShowInput(true)}
              variant="outlined"
              sx={{
                height: 56,
                minWidth: 80,
                lineHeight: "normal",
                padding: "0 16px",
                ":hover": {
                  bgcolor: "transparent",
                  boxShadow: "none",
                },
              }}
            >
              Other
            </Button>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                size="medium"
                placeholder="New Stage"
                value={newCustomStage}
                onChange={(e) => setNewCustomStage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCustomStage();
                  }
                }}
              />
              <Button
                onClick={handleAddCustomStage}
                variant="contained"
                sx={{
                  height: 56,
                  minWidth: 80,
                  lineHeight: "normal",
                  padding: "0 16px",
                }}
              >
                Add
              </Button>
            </Box>
          )}
        </Box>

        <TextField
          label="Description"
          placeholder="Description (use @ to tag)"
          multiline
          rows={2}
          inputRef={descriptionRef}
          value={description}
          onChange={getMentionChangeHandler(
            description,
            setDescription,
            descriptionRef
          )}
        />

        <MentionDropdown />

         

        <TextField
          label="Client Satisfaction (1-5)"
          type="number"
          inputProps={{ min: 1, max: 5 }}
          value={clientSatisfaction}
          onChange={(e) => setClientSatisfaction(e.target.value)}
        />

        <TextField
          label="Feedback"
          placeholder="Feedback (use @ to tag)"
          multiline
          rows={2}
          inputRef={feedbackRef}
          value={feedback}
          onChange={getMentionChangeHandler(feedback, setFeedback, feedbackRef)}
        />

        <MentionDropdown />

        <Box mt={1}>
          <CustomButton
            text="Add Stage"
            onClick={handleAddStage}
            width={"150px"}
          />
        </Box>
      </Stack>
    </Card>
  );
};

export default AddStage;
